import { Fragment, useEffect, useState } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';

import { LinkPreserveSearch } from '@/components/LinkPreserveSearch';
import { RouteConfig } from '@/configs/routes';
import { useNav } from '@/providers/SidebarApp';
import { cls } from '@/utils/cls';

interface SidebarProps {
  items: RouteConfig[];
  iconMap: Record<string, React.ElementType>;
  label?: string;
  expandOnHover?: boolean;
  depth?: number;
  expandablePaths?: string[];
  // Optional section dividers: map of item path → label rendered above that item.
  dividers?: Record<string, string>;
}

export function Sidebar({ items, iconMap, label, expandOnHover, depth, expandablePaths, dividers }: SidebarProps) {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(!!expandOnHover);
  const { maxOpenDepth, prevMaxOpenDepth, notifyMount, notifyUnmount, collapse, expand } = useNav();

  useEffect(() => {
    if (depth !== undefined && depth > 0) {
      notifyMount(depth);
      return () => notifyUnmount(depth);
    }
  }, [depth, notifyMount, notifyUnmount]);

  const isHidden = depth !== undefined && depth > 0 && maxOpenDepth < depth;
  const isRightmost = depth !== undefined && depth > 0 && depth === maxOpenDepth;

  const showRestoreButton = depth === 0 && maxOpenDepth === 0 && prevMaxOpenDepth > 0;

  return (
    <div className="relative shrink-0 flex flex-col">
      <aside
        className={cls(
          'flex-1 flex flex-col bg-[#F5F6F8] border-r border-[#91A2C0]/20 overflow-x-hidden transition-[width] duration-300',
          isHidden ? 'w-0' : collapsed ? 'w-[52px]' : expandOnHover ? 'w-50' : 'w-56',
        )}
        onMouseEnter={expandOnHover ? () => setCollapsed(false) : undefined}
        onMouseLeave={expandOnHover ? () => setCollapsed(true) : undefined}
      >
        {!isHidden && (
          <>
            {isRightmost && (
              <div
                className={cls(
                  'shrink-0 flex items-center h-10 px-2 border-b border-[#91A2C0]/20',
                  collapsed ? 'justify-center' : 'justify-between',
                )}
              >
                {!collapsed && label && (
                  <p className="pl-1 text-[11px] font-semibold uppercase tracking-widest text-[#9295A5]">{label}</p>
                )}
                <button
                  type="button"
                  onClick={() => collapse(depth!)}
                  className="p-1 rounded text-[#9295A5] hover:text-[#274483] transition-colors"
                  aria-label="Collapse navigation"
                >
                  <ChevronLeftIcon className="size-4" />
                </button>
              </div>
            )}

            {!expandOnHover && !isRightmost && !collapsed && label && (
              <div className="shrink-0 flex items-center h-10 px-2 border-b border-[#91A2C0]/20">
                <p className="pl-1 text-[11px] font-semibold uppercase tracking-widest text-[#9295A5]">{label}</p>
              </div>
            )}

            <nav className="flex-1 overflow-y-auto flex flex-col px-2 pt-2 pb-2 space-y-1">
              {items.map((item) => {
                const Icon = iconMap[item.path];
                const isActive = pathname.startsWith(item.path);

                const isExpandable = expandablePaths?.includes(item.path);
                const divider = dividers?.[item.path];

                return (
                  <Fragment key={item.path}>
                    {divider &&
                      (collapsed ? (
                        <div className="mx-2 my-1 border-t border-[#91A2C0]/20" />
                      ) : (
                        <p className="px-3 pt-3 pb-0.5 text-[11px] font-semibold uppercase tracking-widest text-[#9295A5]">
                          {divider}
                        </p>
                      ))}
                    <LinkPreserveSearch
                      to={item.path}
                      aria-current={isActive ? 'page' : undefined}
                      title={collapsed ? item.name : undefined}
                      onClick={isExpandable && depth !== undefined ? () => expand(depth + 1) : undefined}
                      className={cls(
                        'flex items-center gap-2.5 py-2 text-[13px] font-medium transition-colors border-l-2 hover:rounded',
                        collapsed ? 'justify-center px-2' : 'px-3',
                        isActive
                          ? 'border-[#274483] text-[#274483]'
                          : 'border-transparent text-[#5C5F72] hover:text-[#274483] hover:bg-[#91A2C0]/20',
                      )}
                    >
                      {Icon && <Icon className="size-4 shrink-0" />}
                      {!collapsed && <span className="whitespace-nowrap">{item.name}</span>}
                    </LinkPreserveSearch>
                  </Fragment>
                );
              })}
            </nav>
          </>
        )}
      </aside>
      {showRestoreButton && (
        <button
          type="button"
          onClick={() => expand(prevMaxOpenDepth)}
          className="absolute top-[10px] right-0 translate-x-full flex items-center justify-center w-4 h-7 bg-[#F5F6F8] border border-l-0 border-[#91A2C0]/20 rounded-r text-[#9295A5] hover:text-[#274483] transition-colors z-10"
          aria-label="Expand navigation"
        >
          <ChevronRightIcon className="size-3" />
        </button>
      )}
    </div>
  );
}
